import { useState, useEffect, useRef } from 'react';
import { gql, useSubscription } from '@apollo/client';

export interface GroupBuyState {
    confirmedAmount: number;
    reservedAmount: number;
    goalAmount: number;
    status: string;
    unlocksAt: string | null;
}

const GROUPBUY_UPDATE_SUBSCRIPTION = gql`
    subscription GroupBuyUpdate($contentID: String!) {
        groupBuyUpdate(contentID: $contentID) {
            contentID
            confirmedAmount
            reservedAmount
            goalAmount
            status
            unlocksAt
            event
        }
    }
`;

const MIN_VERIFYING_DISPLAY_MS = 60_000;

export function useGroupBuySubscription(
    contentID: string,
    initial: GroupBuyState
): GroupBuyState {
    const [state, setState] = useState<GroupBuyState>(initial);
    const verifyingSinceRef = useRef<number | null>(null);
    const countdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useSubscription<{ groupBuyUpdate: { contentID: string; confirmedAmount?: number; reservedAmount?: number; goalAmount?: number; status?: string; unlocksAt?: string | null; event: string } }>(
        GROUPBUY_UPDATE_SUBSCRIPTION,
        {
            variables: { contentID },
            skip: !contentID,
            onData: ({ data }) => {
                const update = data.data?.groupBuyUpdate;
                if (!update) return;

                const event = update.event;

                if (event === 'gb:progress') {
                    setState(prev => ({
                        confirmedAmount: update.confirmedAmount ?? prev.confirmedAmount,
                        reservedAmount: update.reservedAmount ?? prev.reservedAmount,
                        goalAmount: update.goalAmount ?? prev.goalAmount,
                        status: update.status ?? prev.status,
                        unlocksAt: update.unlocksAt !== undefined ? update.unlocksAt : prev.unlocksAt,
                    }));
                } else if (event === 'gb:goal-reached') {
                    verifyingSinceRef.current = Date.now();
                    setState(prev => ({ ...prev, status: 'verifying' }));
                } else if (event === 'gb:countdown') {
                    const elapsed = Date.now() - (verifyingSinceRef.current ?? 0);
                    const remaining = MIN_VERIFYING_DISPLAY_MS - elapsed;
                    const applyCountdown = () => setState(prev => ({
                        ...prev, status: 'countdown', unlocksAt: update.unlocksAt ?? null,
                    }));
                    if (remaining > 0) {
                        if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
                        countdownTimerRef.current = setTimeout(applyCountdown, remaining);
                    } else {
                        applyCountdown();
                    }
                } else if (event === 'gb:unlocked') {
                    if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
                    setState(prev => ({ ...prev, status: 'unlocked' }));
                }
            },
        }
    );

    // Keep initial values in sync if parent re-fetches
    useEffect(() => {
        setState(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial.confirmedAmount, initial.reservedAmount, initial.goalAmount, initial.status]);

    return state;
}
